import { currencySymbol, fromCents, splitPrefill, toCents } from '@lib/money'
import { GetExpenseResponse, PostExpenseType, PutExpenseType } from '@orbit/api-types'
import type { SplitParticipant, SplitType } from '@orbit/money'

/**
 * The Expense Form core: the Draft the Add/Edit Expense modals hold, its entry
 * states, its transitions, its validation and the payload builders where the
 * Draft crosses into wire format. Pure functions only — no React, no DOM.
 *
 * The split rule itself lives in `@orbit/money`; money conversion in
 * `@lib/money`. This module never reimplements either.
 */

export type { SplitParticipant, SplitType } from '@orbit/money'

/** The split-type switch's options, in display order; `Equally` reads as "Equal" on the control. */
export const SPLIT_TYPE_OPTIONS: ReadonlyArray<{ value: SplitType; label: string }> = [
	{ value: 'Equally', label: 'Equal' },
	{ value: 'Percent', label: 'Percent' },
	{ value: 'Value', label: 'Value' },
]

/**
 * An expense being written. Money stays a decimal string exactly as typed and
 * split shares stay per-user strings, so nothing rounds until the payload is
 * built.
 */
export interface ExpenseDraft {
	name: string
	/** Decimal string from the amount input, e.g. "12.50". Blank until typed. */
	amount: string
	/** Lowercase ISO 4217 code. Always set — the selector shows a default, never a blank. */
	currencyCode: string
	paidByUserId: number
	splitType: SplitType
	/**
	 * Who the expense is split between, in selection order — the order the
	 * participants ride the wire in. Re-selecting someone moves them to the end.
	 */
	participantIds: number[]
	/** Per-user share as typed: whole percents for Percent, decimal money for Value. */
	splitAmounts: Record<number, string>
}

export interface EmptyExpenseDraftOptions {
	/** Everyone on the trip — a new expense starts split between all of them. */
	memberIds: number[]
	paidByUserId: number
	currencyCode: string
}

export const emptyExpenseDraft = ({
	memberIds,
	paidByUserId,
	currencyCode,
}: EmptyExpenseDraftOptions): ExpenseDraft => ({
	name: '',
	amount: '',
	currencyCode,
	paidByUserId,
	splitType: 'Equally',
	participantIds: [...memberIds],
	splitAmounts: {},
})

/**
 * Prefilled per-user shares for a stored expense.
 *
 * Percent rides the wire as whole percents (60 = 60%) and goes straight back
 * into the input. Value rides as integer cents, which the input shows at the
 * currency's display precision — so the shares go through `splitPrefill`
 * together and still add up to the total once rounded. A participant with no
 * stored share (a wire-contract violation) simply gets no prefill.
 */
const prefilledSplitAmounts = (expense: GetExpenseResponse): Record<number, string> => {
	const amounts: Record<number, string> = {}
	if (expense.splitType === 'Equally') return amounts

	const valued = expense.participants.filter(
		(p): p is typeof p & { splitAmount: number } => p.splitAmount != null,
	)
	if (expense.splitType === 'Percent') {
		valued.forEach(p => {
			amounts[p.userId] = String(p.splitAmount)
		})
		return amounts
	}
	const prefilled = splitPrefill(
		valued.map(p => p.splitAmount),
		expense.currencyCode,
	)
	valued.forEach((p, index) => {
		amounts[p.userId] = prefilled[index]
	})
	return amounts
}

/** The Edit Expense entry state: the stored expense, back in Draft form. */
export const expenseDraftFrom = (expense: GetExpenseResponse): ExpenseDraft => ({
	name: expense.name,
	amount: fromCents(expense.amount, expense.currencyCode),
	currencyCode: expense.currencyCode,
	paidByUserId: expense.paidByUserId,
	splitType: expense.splitType,
	participantIds: expense.participants.map(p => p.userId),
	splitAmounts: prefilledSplitAmounts(expense),
})

// MARK: Transitions

export const setName = (draft: ExpenseDraft, name: string): ExpenseDraft => ({ ...draft, name })

export const setAmount = (draft: ExpenseDraft, amount: string): ExpenseDraft => ({
	...draft,
	amount,
})

export const setCurrencyCode = (draft: ExpenseDraft, currencyCode: string): ExpenseDraft => ({
	...draft,
	currencyCode,
})

export const setPaidBy = (draft: ExpenseDraft, paidByUserId: number): ExpenseDraft => ({
	...draft,
	paidByUserId,
})

/**
 * Switches the split type. The typed shares mean something different under each
 * type (percent vs money), so they are dropped rather than reinterpreted.
 */
export const setSplitType = (draft: ExpenseDraft, splitType: SplitType): ExpenseDraft => ({
	...draft,
	splitType,
	splitAmounts: {},
})

export const toggleParticipant = (draft: ExpenseDraft, userId: number): ExpenseDraft => ({
	...draft,
	participantIds: draft.participantIds.includes(userId)
		? draft.participantIds.filter(id => id !== userId)
		: [...draft.participantIds, userId],
})

export const setSplitAmount = (
	draft: ExpenseDraft,
	userId: number,
	value: string,
): ExpenseDraft => ({
	...draft,
	splitAmounts: { ...draft.splitAmounts, [userId]: value },
})

// MARK: Validation

export interface ExpenseValidation {
	/** Does the split add up? Gates the summary line's error styling. */
	splitValid: boolean
	/** The line under the participant list — shown in both the valid and invalid state. */
	splitMessage: string
	/** Every rule the submit button gates on. */
	canSubmit: boolean
}

/** Sum of the selected participants' typed shares, missing ones counting as zero. */
const allocated = (draft: ExpenseDraft): number =>
	draft.participantIds.reduce((sum, id) => sum + parseFloat(draft.splitAmounts[id] || '0'), 0)

/**
 * The live split summary: whether the shares add up, and the sentence that says
 * so. This reads the strings the user is still typing, so it works in decimals
 * with a 0.01 tolerance — `@orbit/money`'s `validateSplit` is the contract gate
 * over the finished integer payload, and stays the authority on the wire rules.
 */
const splitSummary = (draft: ExpenseDraft): Pick<ExpenseValidation, 'splitValid' | 'splitMessage'> => {
	if (draft.participantIds.length === 0) {
		return { splitValid: false, splitMessage: 'Select at least one participant' }
	}
	if (draft.splitType === 'Equally') {
		return {
			splitValid: true,
			splitMessage: `Split evenly between ${draft.participantIds.length} people`,
		}
	}
	const total = allocated(draft)
	if (draft.splitType === 'Percent') {
		if (Math.abs(total - 100) > 0.01) {
			return {
				splitValid: false,
				splitMessage: `Percentages total ${total.toFixed(1)}% (must be 100%)`,
			}
		}
		// The wire carries whole percents (backend requires ints summing to 100),
		// so a fractional percent could never round-trip — block it up front.
		const allWhole = draft.participantIds.every(id =>
			Number.isInteger(parseFloat(draft.splitAmounts[id] || '0')),
		)
		if (!allWhole) {
			return { splitValid: false, splitMessage: 'Percentages must be whole numbers' }
		}
		return { splitValid: true, splitMessage: '100% allocated' }
	}
	const symbol = currencySymbol(draft.currencyCode)
	const expenseAmount = parseFloat(draft.amount || '0')
	if (Math.abs(total - expenseAmount) > 0.01) {
		return {
			splitValid: false,
			splitMessage: `${symbol}${total.toFixed(2)} of ${symbol}${expenseAmount.toFixed(2)} allocated`,
		}
	}
	return { splitValid: true, splitMessage: `${symbol}${total.toFixed(2)} fully allocated` }
}

export const validateExpenseDraft = (draft: ExpenseDraft): ExpenseValidation => {
	const split = splitSummary(draft)
	return {
		...split,
		canSubmit:
			draft.name.trim().length > 0 && parseFloat(draft.amount || '0') > 0 && split.splitValid,
	}
}

// MARK: Payloads

/**
 * The participants the split rides the wire as. Equal splits carry a null
 * amount; Percent carries **whole integer percents** (60 = 60%) — the backend
 * rejects anything that does not sum to exactly 100; Value carries integer
 * hundredths.
 */
const participantsPayload = (draft: ExpenseDraft): SplitParticipant[] => {
	if (draft.splitType === 'Equally') {
		return draft.participantIds.map(userId => ({ userId, splitAmount: null }))
	}
	if (draft.splitType === 'Percent') {
		return draft.participantIds.map(userId => ({
			userId,
			splitAmount: Math.round(parseFloat(draft.splitAmounts[userId] || '0')),
		}))
	}
	return draft.participantIds.map(userId => ({
		userId,
		splitAmount: toCents(draft.splitAmounts[userId] || '0'),
	}))
}

export const toCreatePayload = (draft: ExpenseDraft): PostExpenseType => ({
	name: draft.name.trim(),
	amount: toCents(draft.amount),
	paidByUserId: draft.paidByUserId,
	splitType: draft.splitType,
	participants: participantsPayload(draft),
	currencyCode: draft.currencyCode,
})

/**
 * Every field the PATCH accepts is a control the Edit modal puts on screen, so
 * the whole form goes up each save — there is no untouched field to omit. The
 * one thing that can be *cleared* rather than edited is the receipt image, and
 * that travels on its own endpoints (see {@link expenseImageIntent}).
 */
export const toUpdatePayload = (draft: ExpenseDraft): PutExpenseType => toCreatePayload(draft)

// MARK: Receipt image

/** The receipt as the Edit modal holds it: built once there, and read once by {@link expenseImageIntent}. */
export interface ExpenseImageState {
	/** A file the user just picked, if any. */
	file: File | null
	/** True once the user removed an image that was already persisted. */
	clearExisting?: boolean
	/** The persisted image's file id, when the expense has one. */
	existingFileId?: string
}

/**
 * What the save should do about the receipt image — the omit-vs-clear decision,
 * made once instead of at each call site. Clearing wins over uploading so a
 * removal is never silently undone by a stale pick.
 */
export type ExpenseImageIntent =
	| { kind: 'upload'; file: File }
	| { kind: 'remove'; fileId: string }
	| { kind: 'keep' }

export const expenseImageIntent = ({
	file,
	clearExisting,
	existingFileId,
}: ExpenseImageState): ExpenseImageIntent => {
	if (clearExisting && existingFileId) return { kind: 'remove', fileId: existingFileId }
	if (file) return { kind: 'upload', file }
	return { kind: 'keep' }
}

/** What Add Expense hands its mutation: the request, plus the receipt to attach. */
export interface AddExpenseSubmission {
	payload: PostExpenseType
	imageFile?: File
}

/** What Edit Expense hands its mutation: the request, plus the receipt state the intent is read from. */
export interface EditExpenseSubmission {
	payload: PutExpenseType
	image: ExpenseImageState
}
